import './index.css';
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Row, Col, Space, Modal, Upload, Card, Typography, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom'
import axios from 'axios';
import API_ROUTES from '../../api'
import {
    MailOutlined,
    LockOutlined,
    UserOutlined,
    BankOutlined,
    KeyOutlined,
    PhoneOutlined,
    UploadOutlined,
    QuestionCircleOutlined,
    SolutionOutlined,
    ArrowLeftOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';
import GsrdWch from '../../components/GsrdWch';

const { Title, Text, Link, Paragraph } = Typography;

export default function RegistFirstStep() {

    const navigate = useNavigate();
    const [form] = Form.useForm();
    const initialCountdown = localStorage.getItem('countdown') ? JSON.parse(localStorage.getItem('countdown')) : 0;
    const [countdown, setCountdown] = useState(initialCountdown);
    const [isDisabled, setIsDisabled] = useState(initialCountdown > 0);
    const [certificateUrls, setCertificateUrls] = useState([]);
    const [uploading, setUploading] = useState(false);

    // 仅允许上传图片类机构证明，避免 PDF/Word 等非图片文件进入注册审核流程
    const isProofImageFile = (file) => {
        const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
        const allowedImageExtensions = /\.(jpe?g|png|webp|gif|bmp)$/i;
        return allowedImageTypes.includes(file.type) || allowedImageExtensions.test(file.name || '');
    };

    const beforeUploadProof = (file) => {
        if (!isProofImageFile(file)) {
            Modal.error({ content: 'Please upload an image file (JPG, PNG, WEBP, GIF, or BMP).' });
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleButtonClick = () => {
        const inputEmail = form.getFieldValue('email')
        if (!inputEmail) {
            return Modal.error({ content: 'Please enter your email address first!' });
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inputEmail)) {
            return Modal.error({ content: 'Please enter a valid email address.' });
        }

        axios.post(API_ROUTES.api_auth, { email: inputEmail })
            .then(() => {
                Modal.success({ content: 'Verification code sent to your email!' });
                setCountdown(60);
                setIsDisabled(true);
            })
            .catch(() => {
                Modal.error({ content: 'Failed to send verification code. Please try again.' });
                setIsDisabled(false);
            });
    };

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => {
                    const next = prev - 1;
                    localStorage.setItem('countdown', JSON.stringify(next));
                    return next;
                });
            }, 1000);
        } else {
            setIsDisabled(false);
            localStorage.removeItem('countdown');
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const onFinish = (values) => {
        if (certificateUrls.length === 0) {
            return Modal.error({ content: 'Please upload at least one affiliation verification document!' });
        }

        axios.post(API_ROUTES.api_regist, {
            email: values.email,
            password: values.password,
            realName: values.realName,
            org: values.org,
            secretKey: values.secretKey,
            mobile: values.phoneNumber,
            studentCertificate: certificateUrls.join(';')
        })
            .then(function (response) {
                if (response.data.code === 500) {
                    Modal.error({ content: response.data.msg });
                } else {
                    Modal.success({ content: 'Registration successful! Please log in.' });
                    navigate('/login');
                }
            })
            .catch(function (error) {
                Modal.error({ content: error.response?.data?.msgDes || 'Registration failed' });
            });
    };

    const customRequest = async ({ file, onSuccess, onError }) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(API_ROUTES.api_xiaoai_upload_register, formData);
            if (res.data.code === 200) {
                const newUrl = res.data.data.uploadFile;
                setCertificateUrls(prev => [...prev, newUrl]);
                onSuccess(res.data.data);
                Modal.success({ content: 'Certificate uploaded successfully!' });
            } else {
                onError(new Error(res.data.msg));
                Modal.error({ content: res.data.msg });
            }
        } catch (err) {
            onError(err);
            Modal.error({ content: 'Upload failed' });
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = (file) => {
        const removedUrl = file.response?.uploadFile || file.url;
        setCertificateUrls(prev => prev.filter(url => url !== removedUrl));
    };

    return (
        <div className="shared-auth-page" style={{ backgroundColor: '#f0f5ff', minHeight: '100vh', paddingBottom: '80px' }}>
            {/* Header Section */}
            <div className="shared-auth-hero-banner" style={{
                height: '280px',
                padding: '60px 20px',
                textAlign: 'center',
                color: 'white',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    style={{ color: 'white', position: 'absolute', left: '20px', top: '20px' }}
                    onClick={() => navigate('/login')}
                >
                    Back to Login
                </Button>
                <Title level={1} style={{ color: 'white', margin: 0, fontWeight: 800 }}>Join <GsrdWch /></Title>
                <Paragraph style={{ color: 'rgba(255,255,255,0.8)', marginTop: '8px', fontSize: '16px', maxWidth: '600px' }}>
                    Join a global network of researchers dedicated to uncovering the genomic basis of rare diseases.
                </Paragraph>
            </div>

            {/* Registration Card */}
            <div className="shared-auth-card-wrap">
                <Card
                    bordered={false}
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        borderRadius: '24px',
                        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '40px' }}
                >
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: '#f0f7ff',
                            borderRadius: '50%',
                            margin: '0 auto 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#055878',
                            fontSize: '28px'
                        }}>
                            <UserOutlined />
                        </div>
                        <Title level={3} style={{ color: '#055878', margin: 0 }}>Create Researcher Account</Title>
                        <Text type="secondary">Provide your professional details for institutional verification</Text>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        size="large"
                    >
                        <Row gutter={24}>
                            <Col xs={24} sm={16}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <span>Email Address</span>
                                            <Tooltip title="Please use your official organization email (e.g., @university.edu)">
                                                <QuestionCircleOutlined style={{ color: '#13769a' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name="email"
                                    rules={[
                                        { required: true, message: 'Required' },
                                        { type: 'email', message: 'Invalid format' }
                                    ]}
                                    extra={<Text type="secondary" style={{ fontSize: '12px' }}>Organization email is highly recommended.</Text>}
                                >
                                    <Input prefix={<MailOutlined style={{ color: '#055878' }} />} placeholder="e.g., researcher@ox.ac.uk" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Form.Item label="Verification">
                                    <Button
                                        type="primary"
                                        block
                                        className="verification-code-button"
                                        onClick={handleButtonClick}
                                        disabled={isDisabled}
                                        style={{ background: '#055878', borderColor: '#055878', color: '#fff' }}
                                    >
                                        {isDisabled ? `${countdown}s` : 'Get Code'}
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label="Verification Code"
                            name="secretKey"
                            rules={[{ required: true, message: 'Please enter the code sent to your email' }]}
                        >
                            <Input prefix={<KeyOutlined style={{ color: '#055878' }} />} placeholder="Enter 6-digit code" />
                        </Form.Item>

                        <Row gutter={24}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Full Name"
                                    name="realName"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Input prefix={<UserOutlined style={{ color: '#055878' }} />} placeholder="Your legal name" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Contact Number"
                                    name="phoneNumber"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Input prefix={<PhoneOutlined style={{ color: '#055878' }} />} placeholder="e.g., +86 138..." />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label="Organization / University"
                            name="org"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input prefix={<BankOutlined style={{ color: '#055878' }} />} placeholder="Current institution" />
                        </Form.Item>

                        <Form.Item
                            label="Affiliation Verification Document"
                            required
                            extra="Please upload a photo of your ID card, student card, or employment certificate."
                        >
                            <Upload
                                listType="picture-card"
                                accept="image/*"
                                beforeUpload={beforeUploadProof}
                                customRequest={customRequest}
                                multiple
                                showUploadList={{ showPreviewIcon: true }}
                                onRemove={handleRemove}
                            >
                                {certificateUrls.length < 5 && (
                                    <div>
                                        <UploadOutlined style={{ fontSize: '20px', color: '#055878' }} />
                                        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>Upload Proof</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>

                        <Row gutter={24}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Password"
                                    name="password"
                                    rules={[
                                        { required: true, message: 'Required' },
                                        { min: 8, message: 'Minimum 8 chars' },
                                        {
                                            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
                                            message: 'Must include A, a, and 0-9'
                                        }
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined style={{ color: '#055878' }} />} placeholder="Set password" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Confirm Password"
                                    name="re-password"
                                    dependencies={['password']}
                                    rules={[
                                        { required: true, message: 'Required' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) return Promise.resolve();
                                                return Promise.reject('Passwords do not match');
                                            },
                                        })
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined style={{ color: '#055878' }} />} placeholder="Repeat password" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item style={{ marginTop: '40px', marginBottom: '24px' }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={uploading}
                                style={{
                                    height: '56px',
                                    background: 'linear-gradient(135deg, #055878 0%, #13769a 100%)',
                                    border: 'none',
                                    borderRadius: '28px',
                                    fontWeight: '700',
                                    fontSize: '18px',
                                    boxShadow: '0 8px 20px rgba(5, 88, 120, 0.25)'
                                }}
                            >
                                Submit Registration
                            </Button>
                        </Form.Item>

                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">Already have an account? </Text>
                            <Link onClick={() => navigate('/login')} style={{ color: '#055878', fontWeight: 600 }}>Log in here</Link>
                        </div>
                    </Form>
                </Card>
            </div>
        </div>
    );
}
